"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
interface Step {
  key: string;
  label: string;
  description: string;
  href: string;
  cta: string;
  icon: React.ReactNode;
}

function getSteps(firstDeckId: string | null): Step[] {
  const deckPath = firstDeckId ? `/study/${firstDeckId}` : "/upload";

  return [
    {
      key: "uploaded",
      label: "Upload your first document",
      description: "Drop a PDF or Word doc and watch AI turn it into flashcards",
      href: "/upload",
      cta: "Upload Notes",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" x2="12" y1="3" y2="15" />
        </svg>
      ),
    },
    {
      key: "studied",
      label: "Complete your first study session",
      description: "Flip through cards with spaced repetition",
      href: deckPath,
      cta: "Study Deck",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="6 3 20 12 6 21 6 3" />
        </svg>
      ),
    },
  ];
}

export const ONBOARDING_STORAGE_KEY = "snapcards-onboarding";

interface OnboardingState {
  dismissed: boolean;
  completed: Record<string, boolean>;
}

function getState(): OnboardingState {
  if (typeof window === "undefined") return { dismissed: false, completed: {} };
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { dismissed: false, completed: {} };
}

function saveState(state: OnboardingState) {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

interface GettingStartedChecklistProps {
  hasDecks: boolean;
  hasStudied: boolean;
  firstDeckId: string | null;
}

export function GettingStartedChecklist({ hasDecks, hasStudied, firstDeckId }: GettingStartedChecklistProps) {
  const [state, setState] = useState<OnboardingState>({ dismissed: false, completed: {} });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const s = getState();
    // Auto-mark steps based on actual user data
    if (hasDecks) s.completed.uploaded = true;
    if (hasStudied) s.completed.studied = true;
    saveState(s);
    setState(s);
    setMounted(true);
  }, [hasDecks, hasStudied]);

  const steps = getSteps(firstDeckId);

  if (!mounted || state.dismissed) return null;

  const completedCount = steps.filter((s) => state.completed[s.key]).length;
  const allDone = completedCount === steps.length;

  if (allDone) return null;

  const markComplete = (key: string) => {
    const next = { ...state, completed: { ...state.completed, [key]: true } };
    saveState(next);
    setState(next);
  };

  const dismiss = () => {
    const next = { ...state, dismissed: true };
    saveState(next);
    setState(next);
  };

  const progress = (completedCount / steps.length) * 100;

  return (
    <div className="bg-white border-2 border-border-thick rounded-xl shadow-brutal p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-base font-bold">Get started with SnapCards</h2>
          <p className="font-body text-xs text-border-thick/50 mt-0.5">
            {completedCount} of {steps.length} steps completed
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-border-thick/30 hover:text-border-thick/60 focus-visible:outline-2 focus-visible:outline-primary transition-colors cursor-pointer p-3 -m-2"
          aria-label="Dismiss getting started checklist"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-retro-gray/30 rounded-full mb-5 border border-border-thick/10">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step) => {
          const done = state.completed[step.key];
          return (
            <div
              key={step.key}
              className={
                "flex items-center gap-3.5 p-3 rounded-lg border-2 transition-all " +
                (done
                  ? "border-success/30 bg-success/5"
                  : "border-border-thick/15 bg-white hover:border-primary/30")
              }
            >
              {/* Checkbox */}
              <div
                className={
                  "w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors duration-200 " +
                  (done
                    ? "bg-success border-success text-white"
                    : "border-border-thick/20 bg-white")
                }
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span className="text-border-thick/20">{step.icon}</span>
                )}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className={"font-display text-sm font-semibold " + (done ? "text-border-thick/50 line-through" : "")}>
                  {step.label}
                </p>
                <p className="font-body text-xs text-border-thick/50 mt-0.5">{step.description}</p>
              </div>

              {/* CTA */}
              {!done && (
                <Link href={step.href} className="shrink-0" onClick={() => markComplete(step.key)}>
                  <Button size="sm" variant="ghost" className="text-xs">
                    {step.cta}
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
